import { allQuestions } from '@submitpro/shared';
import { useMemo, useState } from 'react';
import { ProgressBar } from '../../components/intake-form/ProgressBar';
import { QuestionRenderer } from '../../components/intake-form/QuestionRenderer';
import { useConversationEngine } from '../../lib/conversation-engine-hook';

// ---------------------------------------------------------------------------
// Agent-Mode Intake — embedded inside the dashboard
// ---------------------------------------------------------------------------
// Producer-facing. Sidebar with sections, can jump around. Adds internal notes.
// ---------------------------------------------------------------------------

export default function AgentIntake() {
  const engine = useConversationEngine(allQuestions, 'agent');
  const [error, setError] = useState<string | undefined>(undefined);

  const current = engine.currentQuestion;
  const currentAnswer = current ? engine.answers[current.id] : undefined;

  // Group active questions by section for sidebar
  const sections = useMemo(() => {
    const map: Record<string, { count: number; answered: number }> = {};
    for (const q of engine.activeQuestions) {
      if (!map[q.section]) map[q.section] = { count: 0, answered: 0 };
      map[q.section].count++;
      if (engine.answers[q.id] !== undefined) map[q.section].answered++;
    }
    return map;
  }, [engine.activeQuestions, engine.answers]);

  function handleContinue() {
    if (!current) return;
    if (
      current.required &&
      (currentAnswer === undefined || currentAnswer === '' || currentAnswer === null)
    ) {
      setError('This field is required');
      return;
    }
    setError(undefined);
    engine.answer(currentAnswer);
  }

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Sidebar: section nav */}
      <aside className="w-72 bg-slate-50 border-r border-slate-200 overflow-y-auto p-4">
        <div className="mb-4">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            New Submission
          </h2>
          <ProgressBar percentage={engine.progress.percentage} />
        </div>

        <nav className="space-y-1">
          {Object.entries(sections).map(([section, stats]) => {
            const isCurrent = current?.section === section;
            const isComplete = stats.answered === stats.count;
            return (
              <div
                key={section}
                className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${
                  isCurrent
                    ? 'bg-blue-100 text-blue-900 font-medium'
                    : 'text-slate-700 hover:bg-white'
                }`}
              >
                <span className="capitalize">{section.replace('_', ' ')}</span>
                <span
                  className={`text-xs ${
                    isComplete ? 'text-emerald-600 font-medium' : 'text-slate-500'
                  }`}
                >
                  {stats.answered}/{stats.count}
                </span>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main area */}
      <main className="flex-1 overflow-y-auto bg-white p-8">
        {current ? (
          <div className="max-w-2xl mx-auto">
            <QuestionRenderer
              key={current.id}
              question={current}
              value={currentAnswer}
              onChange={(v) => {
                setError(undefined);
                engine.answers[current.id] = v;
              }}
              error={error}
            />

            {/* Agent-only: internal note for this question */}
            <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 p-3">
              <label
                htmlFor="agent-internal-note"
                className="block text-xs font-medium text-amber-900 mb-1"
              >
                Internal note (not visible to prospect)
              </label>
              <textarea
                id="agent-internal-note"
                placeholder="Add context, follow-up reminder, etc..."
                rows={2}
                className="w-full text-sm rounded border border-amber-300 px-3 py-2 bg-white"
              />
            </div>

            <div className="mt-6 flex items-center justify-between">
              <button
                type="button"
                onClick={engine.back}
                className="text-slate-600 hover:text-slate-900 text-sm font-medium"
              >
                ← Previous
              </button>
              <div className="flex gap-2">
                {!current.required && (
                  <button
                    type="button"
                    onClick={engine.skip}
                    className="text-slate-600 hover:text-slate-900 text-sm font-medium px-4 py-2"
                  >
                    Skip
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleContinue}
                  className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 font-medium transition"
                >
                  Continue →
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto text-center py-12">
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Submission complete</h2>
            <p className="text-slate-600 mb-6">
              All required questions answered. Review and save to the pipeline.
            </p>
            <button
              type="button"
              className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 font-medium"
            >
              Save to pipeline
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
